/**
 * Robust PDF export utilities for Division 7 Preview.
 * Uses html2pdf.js (html2canvas + jsPDF) for client-side HTML → PDF conversion.
 *
 * KEY FIX: Pass HTML as a STRING to html2pdf.js rather than a DOM element.
 * html2pdf.js internally clones DOM elements and inherits offscreen positioning,
 * resulting in blank captures. By passing a string, html2pdf creates its OWN
 * container element, positions it in the viewport, and captures it properly.
 */

export function sanitizeFilename(name) {
  return name.replace(/[^a-zA-Z0-9 ._-]/g, '_').replace(/_+/g, '_').trim() || 'product';
}

export function buildPdfFilename(product) {
  const parts = [product.manufacturerName, product.productNameModel].filter(Boolean);
  return sanitizeFilename(parts.join(' - ')) + '.pdf';
}

/* ── helpers ─────────────────────────────────────────────── */

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/** Collect all stylesheets from the document as inline <style> blocks. */
function collectStyles() {
  const sheets = [];
  for (const sheet of document.styleSheets) {
    try {
      const rules = Array.from(sheet.cssRules || []).map((r) => r.cssText).join('\n');
      if (rules) sheets.push(rules);
    } catch {
      // Cross-origin stylesheet – skip (Tailwind is usually same-origin)
    }
  }
  return sheets.join('\n');
}

/** Strip interactive elements from HTML string. */
function stripInteractiveFromHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  tmp.querySelectorAll('button, iframe, object, embed, video, audio, [data-no-pdf]').forEach((n) => n.remove());
  // Remove print:hidden elements
  tmp.querySelectorAll('.print\\:hidden, [class*="print:hidden"]').forEach((n) => n.remove());
  // Strip clipping classes for clean PDF rendering
  tmp.querySelectorAll('.truncate, .line-clamp-1, .line-clamp-2, .line-clamp-3').forEach((el) => {
    el.classList.remove('truncate', 'line-clamp-1', 'line-clamp-2', 'line-clamp-3');
  });
  return tmp.innerHTML;
}

/* ── main capture function ───────────────────────────────── */

/**
 * PDF-specific CSS overrides.
 * These ONLY apply inside the PDF capture — the on-screen modal is untouched.
 * Fixes:
 *  - Vertical text clipping on submittal rows (truncate → overflow visible)
 *  - Adequate line-height so descenders/ascenders are not cut
 *  - Proper padding for breathing room
 */
const PDF_OVERRIDES = `
  /* ══════════════════════════════════════════════════════
     html2canvas VERTICAL CLIPPING FIX
     Root cause: html2canvas calculates bounding boxes too
     tightly for text, cutting off ascenders/descenders.
     Fix: Force generous line-height + explicit padding on
     every text-containing element.
     ══════════════════════════════════════════════════════ */

  /* ── GLOBAL: overflow visible on everything ── */
  .print-view,
  .print-view *,
  .print-view *::before,
  .print-view *::after {
    overflow: visible !important;
    text-overflow: clip !important;
    box-sizing: border-box !important;
  }

  /* ── GLOBAL: base container ── */
  .print-view {
    transform: none !important;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif !important;
    -webkit-font-smoothing: antialiased !important;
    color: #1e293b !important;
  }

  /* ── ALL TEXT ELEMENTS: generous line-height + padding ── */
  .print-view h1,
  .print-view h2,
  .print-view h3,
  .print-view h4,
  .print-view h5,
  .print-view h6,
  .print-view p,
  .print-view span,
  .print-view a,
  .print-view label,
  .print-view td,
  .print-view th,
  .print-view li {
    line-height: 1.7 !important;
    padding-top: 1px !important;
    padding-bottom: 2px !important;
  }

  /* ── Headings ── */
  .print-view h1 {
    font-size: 18px !important;
    font-weight: 700 !important;
    margin-bottom: 2px !important;
    padding-top: 2px !important;
    padding-bottom: 2px !important;
  }
  .print-view h3 {
    font-size: 13px !important;
    font-weight: 600 !important;
    padding-bottom: 6px !important;
    padding-top: 3px !important;
    margin-bottom: 8px !important;
  }
  .print-view h5 {
    font-size: 12px !important;
    font-weight: 600 !important;
    padding-top: 2px !important;
    padding-bottom: 2px !important;
  }

  /* ── Section spacing + page break avoidance ── */
  .print-view .mb-4 {
    margin-bottom: 14px !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  /* ── Grid rows: force 2-col layout for PDF ──
     All PrintViews use "grid grid-cols-1 sm:grid-cols-2".
     html2canvas may not honour responsive breakpoints,
     so we force the 2-column layout explicitly. */
  .print-view .grid {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 4px 16px !important;
  }
  .print-view .grid > div {
    min-height: 24px !important;
    padding-top: 2px !important;
    padding-bottom: 2px !important;
    break-inside: avoid !important;
  }

  /* ── Label/value flex rows: force horizontal layout ──
     All PrintViews use "flex flex-col sm:flex-row" inside grid cells. */
  .print-view .grid > div > .flex {
    flex-direction: row !important;
  }
  .print-view .grid .flex.flex-col {
    flex-direction: row !important;
  }

  /* ── Text sizing ── */
  .print-view .text-xs {
    font-size: 11px !important;
    line-height: 1.7 !important;
  }
  .print-view .text-sm {
    font-size: 13px !important;
    line-height: 1.7 !important;
  }
  .print-view .text-\\[10px\\] {
    font-size: 10px !important;
    line-height: 1.7 !important;
  }
  .print-view .text-\\[8px\\] {
    font-size: 8px !important;
    line-height: 1.7 !important;
  }
  .print-view .text-lg {
    font-size: 18px !important;
    line-height: 1.7 !important;
  }

  /* ── Label widths: force fixed width for PDF ──
     Responsive sm:w-40 may not apply; force it. */
  .print-view [class*="w-40"],
  .print-view [class*="sm\\:w-40"] {
    width: 160px !important;
    min-width: 160px !important;
    flex-shrink: 0 !important;
  }
  .print-view .w-36,
  .print-view [class*="sm\\:w-36"] {
    width: 144px !important;
    min-width: 144px !important;
    flex-shrink: 0 !important;
  }

  /* ── Value text: allow wrapping ── */
  .print-view .break-words {
    word-break: break-word !important;
    overflow-wrap: anywhere !important;
  }

  /* ── Truncate fix ── */
  .print-view .truncate {
    overflow: visible !important;
    text-overflow: clip !important;
    white-space: normal !important;
    word-break: break-word !important;
  }

  /* ── Submittal rows: generous height ── */
  .print-view .space-y-1\\.5 > div,
  .print-view .space-y-1\\.5 > [class] {
    min-height: 28px !important;
    padding-top: 4px !important;
    padding-bottom: 4px !important;
  }

  /* ── All flex rows: alignment + min height ── */
  .print-view .flex {
    min-height: 20px !important;
  }
  .print-view .flex.items-center {
    min-height: 28px !important;
  }

  /* ── Force header row layout ── */
  .print-view > div:first-child {
    display: flex !important;
    flex-direction: row !important;
    align-items: flex-start !important;
    justify-content: space-between !important;
  }

  /* ── Status badge + parent chain ── */
  /* Badge elements — all styling is applied via inline !important
     in stripClippingClasses, but CSS rules here act as a safety net */
  .print-view .bg-green-600,
  .print-view .bg-red-600,
  .print-view .bg-amber-100 {
    font-size: 10px !important;
    line-height: 1 !important;
    display: inline-block !important;
    text-align: center !important;
    overflow: visible !important;
    height: auto !important;
    transform: none !important;
    box-sizing: border-box !important;
    position: static !important;
    vertical-align: middle !important;
    padding: 1px 8px 4px 8px !important;
  }
  /* Badge parent span — override the generic span rules */
  .print-view span:has(> .bg-green-600),
  .print-view span:has(> .bg-red-600),
  .print-view span:has(> .bg-amber-100) {
    display: inline-flex !important;
    align-items: center !important;
    line-height: 1 !important;
    overflow: visible !important;
    height: auto !important;
    padding: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  /* ── Header area ── */
  .print-view .pb-4 {
    padding-bottom: 12px !important;
    border-bottom: 1px solid #e2e8f0;
  }
  .print-view .mb-6 {
    margin-bottom: 18px !important;
  }

  /* ── Footer ── */
  .print-view .border-t {
    border-top: 1px solid #e2e8f0 !important;
    padding-top: 10px !important;
    margin-top: 14px !important;
  }

  /* ── SVG icons: do not apply text line-height ── */
  .print-view svg {
    overflow: visible !important;
    line-height: normal !important;
    padding: 0 !important;
  }

  /* ── Color consistency ── */
  .print-view .text-slate-500 { color: #64748b !important; }
  .print-view .text-slate-400 { color: #94a3b8 !important; }
  .print-view .text-slate-600 { color: #475569 !important; }
  .print-view .text-slate-700 { color: #334155 !important; }
  .print-view .text-slate-800 { color: #1e293b !important; }
  .print-view .text-slate-900 { color: #0f172a !important; }
  .print-view .bg-green-600 { background-color: #16a34a !important; color: #fff !important; }
  .print-view .bg-red-600 { background-color: #dc2626 !important; color: #fff !important; }
  .print-view .bg-amber-100 { background-color: #fef3c7 !important; }
  .print-view .text-amber-700 { color: #b45309 !important; }
  .print-view .border-slate-200 { border-color: #e2e8f0 !important; }
`;

/**
 * Normalise the DOM for html2canvas PDF rendering.
 *
 * Root cause of all text-clipping bugs: html2canvas has its own CSS
 * parser that does NOT reliably honour rules from <style> blocks,
 * especially complex selectors, responsive utilities (sm:, print:),
 * or pseudo-elements. It DOES reliably read **inline styles**.
 *
 * Therefore the only dependable fix is to walk the DOM and apply
 * every critical rendering property as an inline style BEFORE
 * html2canvas measures bounding boxes.
 *
 * This function is the single, shared fix for all Division 7
 * PrintViews (Air Barriers, Thermal Insulation, Roofing, Flashing,
 * Joint Protection, Weather Barriers).
 */
function stripClippingClasses(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;

  // ── 1. Remove truncation / clamp classes ──
  tmp.querySelectorAll('.truncate, .line-clamp-1, .line-clamp-2, .line-clamp-3').forEach((el) => {
    el.classList.remove('truncate', 'line-clamp-1', 'line-clamp-2', 'line-clamp-3');
    Object.assign(el.style, {
      overflow: 'visible',
      textOverflow: 'clip',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    });
  });

  // ── 2. Force overflow:visible on EVERY element ──
  //    html2canvas clips at the computed overflow boundary;
  //    inline visible guarantees nothing is masked.
  tmp.querySelectorAll('*').forEach((el) => {
    el.style.overflow = 'visible';
  });

  // ── 3. Text elements: generous line-height + bottom pad ──
  tmp.querySelectorAll('h1,h2,h3,h4,h5,h6,p,span,a,label,td,th,li').forEach((el) => {
    Object.assign(el.style, {
      lineHeight: '1.8',
      paddingBottom: '2px',
    });
  });

  // ── 4. Section headings: extra breathing room ──
  tmp.querySelectorAll('h3').forEach((el) => {
    Object.assign(el.style, {
      lineHeight: '1.8',
      paddingTop: '3px',
      paddingBottom: '6px',
      marginBottom: '8px',
    });
  });

  // ── 5. Force 2-col grid (responsive sm:grid-cols-2 unreliable) ──
  tmp.querySelectorAll('.grid').forEach((el) => {
    Object.assign(el.style, {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: '6px 16px',
    });
  });

  // ── 6. Grid children: adequate row height ──
  tmp.querySelectorAll('.grid > div').forEach((el) => {
    Object.assign(el.style, {
      minHeight: '26px',
      paddingTop: '2px',
      paddingBottom: '3px',
      breakInside: 'avoid',
    });
  });

  // ── 7. Force flex-row on label:value pairs (sm:flex-row unreliable) ──
  tmp.querySelectorAll('.flex.flex-col').forEach((el) => {
    el.classList.remove('flex-col');
    el.style.flexDirection = 'row';
  });

  // ── 8. Label widths (sm:w-40, sm:w-36, w-36 unreliable) ──
  tmp.querySelectorAll('[class*="w-40"]').forEach((el) => {
    Object.assign(el.style, { width: '160px', minWidth: '160px', flexShrink: '0' });
  });
  tmp.querySelectorAll('[class*="w-36"]').forEach((el) => {
    Object.assign(el.style, { width: '144px', minWidth: '144px', flexShrink: '0' });
  });
  tmp.querySelectorAll('[class*="shrink-0"]').forEach((el) => {
    el.style.flexShrink = '0';
  });

  // ── 9. Badge / status pill — DEFINITIVE FIX ──
  //
  //    ROOT CAUSE IDENTIFIED:
  //    The CSS PDF_OVERRIDES block contains:
  //      .print-view span { line-height: 1.7 !important; padding-top: 1px !important; ... }
  //    CSS !important rules OVERRIDE normal inline styles (Object.assign).
  //    So all previous fixes were silently overridden by the stylesheet.
  //
  //    FIX: Use el.style.setProperty(prop, val, 'important') for ALL badge
  //    inline styles. Inline !important always beats stylesheet !important.
  //    No inner span wrappers needed — keep the DOM simple.
  //
  tmp.querySelectorAll('.bg-green-600, .bg-red-600, .bg-amber-100').forEach((badge) => {
    // Strip any existing inline styles to start clean
    badge.removeAttribute('style');

    const s = badge.style;
    s.setProperty('display', 'inline-block', 'important');
    s.setProperty('text-align', 'center', 'important');
    s.setProperty('font-size', '10px', 'important');
    s.setProperty('border-radius', '4px', 'important');
    s.setProperty('overflow', 'visible', 'important');
    s.setProperty('height', 'auto', 'important');
    s.setProperty('box-sizing', 'border-box', 'important');
    s.setProperty('position', 'static', 'important');
    s.setProperty('transform', 'none', 'important');
    s.setProperty('vertical-align', 'middle', 'important');
    // line-height:1 with asymmetric padding: more bottom to push
    // background downward relative to text (compensates html2canvas
    // rendering background ~2px too high).
    s.setProperty('line-height', '1', 'important');
    s.setProperty('padding', '1px 8px 4px 8px', 'important');

    // Ensure text content is NOT wrapped in extra spans
    // (undo any previous inner-span hacks)
    const textContent = badge.textContent;
    badge.textContent = textContent;

    // Set background color with !important based on class
    if (badge.classList.contains('bg-green-600')) {
      s.setProperty('background-color', '#16a34a', 'important');
      s.setProperty('color', '#ffffff', 'important');
    } else if (badge.classList.contains('bg-red-600')) {
      s.setProperty('background-color', '#dc2626', 'important');
      s.setProperty('color', '#ffffff', 'important');
    } else if (badge.classList.contains('bg-amber-100')) {
      s.setProperty('background-color', '#fef3c7', 'important');
      s.setProperty('color', '#b45309', 'important');
    }

    // Fix the parent span — must also use !important to override
    // the .print-view span { ... !important } CSS rules
    const parent = badge.parentElement;
    if (parent && parent.tagName === 'SPAN') {
      parent.removeAttribute('style');
      const ps = parent.style;
      ps.setProperty('display', 'inline-flex', 'important');
      ps.setProperty('align-items', 'center', 'important');
      ps.setProperty('line-height', '1', 'important');
      ps.setProperty('overflow', 'visible', 'important');
      ps.setProperty('height', 'auto', 'important');
      ps.setProperty('padding', '0', 'important');
    }

    // Fix grandparent flex row
    const grandparent = parent?.parentElement;
    if (grandparent && grandparent.style) {
      grandparent.style.setProperty('overflow', 'visible', 'important');
      grandparent.style.setProperty('align-items', 'center', 'important');
    }
  });

  // ── 10. Submittal / space-y rows ──
  tmp.querySelectorAll('[class*="space-y"] > div, [class*="space-y"] > [class]').forEach((el) => {
    Object.assign(el.style, {
      minHeight: '30px',
      paddingTop: '5px',
      paddingBottom: '5px',
      lineHeight: '1.8',
    });
  });

  // ── 11. break-words / word wrap ──
  tmp.querySelectorAll('.break-words, [class*="overflow-wrap"]').forEach((el) => {
    Object.assign(el.style, {
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
      whiteSpace: 'normal',
    });
  });

  // ── 12. Sections: page-break avoidance ──
  tmp.querySelectorAll('.mb-4').forEach((el) => {
    Object.assign(el.style, {
      breakInside: 'avoid',
      pageBreakInside: 'avoid',
      marginBottom: '14px',
    });
  });

  // ── 13. SVG icons: reset text rules ──
  tmp.querySelectorAll('svg').forEach((el) => {
    Object.assign(el.style, {
      lineHeight: 'normal',
      padding: '0',
      overflow: 'visible',
    });
  });

  // ── 14. Header row: force horizontal layout ──
  const firstChild = tmp.querySelector('.print-view > div:first-child, .flex.sm\\:flex-row.sm\\:items-start');
  if (firstChild) {
    Object.assign(firstChild.style, {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
    });
  }

  return tmp.innerHTML;
}

/**
 * Generate a PDF from an HTML string.
 * html2pdf.js creates its own container in the viewport, so no offscreen issues.
 */
async function htmlStringToPdf(htmlContent, filename) {
  const html2pdf = (await import('html2pdf.js')).default;

  // Strip classes that cause html2canvas vertical clipping
  const cleanedHtml = stripClippingClasses(htmlContent);

  // Wrap the content with inline styles so html2pdf's container picks up Tailwind classes
  const css = collectStyles();
  const wrappedHtml = `
    <style>${css}</style>
    <style>${PDF_OVERRIDES}</style>
    <div class="print-view" style="width:730px;background:#fff;padding:0;margin:0;">${cleanedHtml}</div>
  `;

  await html2pdf().set({
    margin: [8, 8, 8, 8],
    filename,
    image: { type: 'jpeg', quality: 0.92 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: '#ffffff',
      scrollY: 0,
      scrollX: 0,
    },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    pagebreak: { mode: ['css', 'legacy'] },
  }).from(wrappedHtml, 'string').save();
}

/* ── public API ──────────────────────────────────────────── */

/**
 * Generate PDF from a ref-based element (list download flow).
 * Waits for element to render, then extracts its innerHTML and passes as string.
 */
export async function generatePdfFromElement(element, filename) {
  if (!element) throw new Error('PDF container element is null');

  // Wait for React render to stabilise
  await wait(200);

  const html = element.innerHTML;
  if (!html || html.trim().length < 50) {
    throw new Error('PDF container appears empty');
  }

  await htmlStringToPdf(html, filename);
}

/**
 * Generate PDF from the modal preview content (modal download flow).
 * Reads the on-screen modal content, strips interactive elements, and renders.
 */
export async function generatePdfFromModalContent(elementId, filename) {
  const source = document.getElementById(elementId);
  if (!source) throw new Error(`Modal content element #${elementId} not found`);

  const rawHtml = source.innerHTML;
  if (!rawHtml || rawHtml.trim().length < 50) {
    throw new Error('Modal content appears empty');
  }

  const cleanHtml = stripInteractiveFromHtml(rawHtml);
  await htmlStringToPdf(cleanHtml, filename);
}
